import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ArrowRight, ShieldCheck, Truck, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { createCheckout } from '../lib/api';
import { toast } from 'sonner';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France'
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email || !formData.address || !formData.city || !formData.postal_code) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setIsCheckingOut(true);

    try {
      const checkoutData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          bundle_id: item.bundle_id,
          quantity: item.quantity
        })),
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        shipping_address: {
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          country: formData.country
        }
      };

      const response = await createCheckout(checkoutData);
      
      if (response.url) {
        // Redirect to Stripe checkout
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const cartTotal = getCartTotal();
  const shipping = 0; // Free shipping
  const total = cartTotal + shipping;

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  if (cart.length === 0) {
    return (
      <div data-testid="cart-page-empty" className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-ghrb-black mb-4">
            Votre panier est vide
          </h1>
          <p className="font-jakarta text-ghrb-gray-dark mb-8">
            Découvrez nos produits et ajoutez-les à votre panier.
          </p>
          <Link to="/produits">
            <Button 
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white font-jakarta font-semibold px-8 py-6 rounded-full"
              data-testid="continue-shopping-btn"
            >
              Voir les produits
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-12 lg:py-16 bg-ghrb-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1 
            className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight text-ghrb-black"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            Votre panier ({cart.length})
          </motion.h1>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cart.map((item, index) => (
                <motion.div 
                  key={item.itemId}
                  className="flex gap-6 p-6 bg-ghrb-gray rounded-2xl cart-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`cart-item-${index}`}
                >
                  {/* Product Image */}
                  <Link to={`/produits/${item.product_slug}`} className="flex-shrink-0">
                    <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-xl overflow-hidden bg-white">
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/produits/${item.product_slug}`}>
                      <h3 className="font-outfit text-lg font-semibold text-ghrb-black mb-1 hover:text-ghrb-orange transition-colors">
                        {item.product_name}
                      </h3>
                    </Link>
                    {item.bundle_name && (
                      <p className="font-jakarta text-sm text-ghrb-gray-dark mb-3">
                        {item.bundle_name}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.itemId, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center bg-white rounded-full hover:bg-ghrb-black hover:text-white transition-colors"
                          data-testid={`decrease-qty-${index}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-jakarta font-medium w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.itemId, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center bg-white rounded-full hover:bg-ghrb-black hover:text-white transition-colors"
                          data-testid={`increase-qty-${index}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Price & Remove */}
                      <div className="flex items-center gap-4">
                        <p className="font-outfit text-lg font-bold text-ghrb-black">
                          {(item.price * item.quantity).toFixed(2)}€
                        </p>
                        <button
                          onClick={() => removeFromCart(item.itemId)}
                          className="text-ghrb-gray-dark hover:text-red-500 transition-colors"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-ghrb-gray p-6 lg:p-8 rounded-2xl">
                <h2 className="font-outfit text-xl font-semibold text-ghrb-black mb-6">
                  Récapitulatif
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between font-jakarta">
                    <span className="text-ghrb-gray-dark">Sous-total</span>
                    <span className="text-ghrb-black font-medium">{cartTotal.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between font-jakarta">
                    <span className="text-ghrb-gray-dark">Livraison</span>
                    <span className="text-ghrb-orange font-medium">Gratuite</span>
                  </div>
                  <div className="border-t border-ghrb-black/10 pt-4 flex justify-between">
                    <span className="font-outfit font-semibold text-ghrb-black">Total</span>
                    <span className="font-outfit text-xl font-bold text-ghrb-black">{total.toFixed(2)}€</span>
                  </div>
                </div>

                {!showCheckoutForm ? (
                  <Button 
                    size="lg"
                    onClick={() => setShowCheckoutForm(true)}
                    className="w-full bg-ghrb-orange hover:bg-ghrb-orange-hover text-white font-jakarta font-semibold py-6 rounded-full transition-transform hover:scale-[1.02]"
                    data-testid="proceed-checkout-btn"
                  >
                    Passer la commande
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <form onSubmit={handleCheckout} className="space-y-4">
                    <div>
                      <Label htmlFor="customer_name" className="font-jakarta text-sm">Nom complet</Label>
                      <Input
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        placeholder="Jean Dupont"
                        className="mt-1"
                        data-testid="checkout-name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer_email" className="font-jakarta text-sm">Email</Label>
                      <Input
                        id="customer_email"
                        name="customer_email"
                        type="email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        placeholder="jean@exemple.fr"
                        className="mt-1"
                        data-testid="checkout-email"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="address" className="font-jakarta text-sm">Adresse</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="123 Rue de Paris"
                        className="mt-1"
                        data-testid="checkout-address"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="city" className="font-jakarta text-sm">Ville</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Paris"
                          className="mt-1"
                          data-testid="checkout-city"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="postal_code" className="font-jakarta text-sm">Code postal</Label>
                        <Input
                          id="postal_code"
                          name="postal_code"
                          value={formData.postal_code}
                          onChange={handleInputChange}
                          placeholder="75001"
                          className="mt-1"
                          data-testid="checkout-postal"
                          required
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit"
                      size="lg"
                      disabled={isCheckingOut}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-jakarta font-semibold py-6 rounded-full transition-transform hover:scale-[1.02] disabled:opacity-50"
                      data-testid="pay-now-btn"
                    >
                      {isCheckingOut ? 'Chargement...' : `Payer ${total.toFixed(2)}€`}
                    </Button>
                  </form>
                )}

                {/* Trust badges */}
                <div className="mt-6 pt-6 border-t border-ghrb-black/10 space-y-3">
                  {[
                    { icon: ShieldCheck, text: 'Paiement 100% sécurisé' },
                    { icon: Truck, text: 'Livraison rapide et gratuite' },
                    { icon: RotateCcw, text: 'Retours sous 30 jours' }
                  ].map((badge, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm">
                      <badge.icon className="w-5 h-5 text-amber-500" />
                      <span className="font-jakarta text-ghrb-gray-dark">{badge.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;
